import { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Form, Button, Card, Pagination, Badge } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import Product from "../Components/Product";
import {
  useGetProductsQuery,
  useGetFitmentFacetsQuery,
  useGetPartCategoriesQuery,
} from "../slices/productsApiSlice";
import Loader from "../Components/Loader";
import Message from "../Components/Message";
import Meta from "../Components/Meta";
import BackButton from "../Components/BackButton";
import { loadSelectedGarageVehicle } from "../utils/garage";
import { useSelector } from "react-redux";
import { categoryBySlug, normalizePublicUrl, categorySchemas } from '../seo/policy';

const AllProductsScreen = () => {

  const { keyword, categorySlug } = useParams();
  const seoCategory = categoryBySlug(categorySlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useSelector((state) => state.auth);
  const isTieredUser = Boolean(userInfo?.tier?.id);

  // ✅ Applied filters are stored in URL (source of truth)
  const applied = useMemo(() => {
    const make = (searchParams.get("make") || "").trim();
    const model = (searchParams.get("model") || "").trim();
    const year = (searchParams.get("year") || "").trim();
    const trim = (searchParams.get("trim") || "").trim();
    const category = (searchParams.get("category") || "").trim();
    const pageNumber = Number(searchParams.get("page") || searchParams.get("pageNumber") || 1);

    return { make, model, year, trim, category, pageNumber };
  }, [searchParams]);

  // Controlled UI state (draft)
  const [veh, setVeh] = useState({
    make: applied.make,
    model: applied.model,
    year: applied.year,
    trim: applied.trim,
  });
  const [category, setCategory] = useState(applied.category);

  // When user touches filters, flip this on (optional, but useful)
  const [manualOverride, setManualOverride] = useState(false);

  const didInitRef = useRef(false);

  // =========================================================
  // ✅ 0) INITIAL PREFILL FROM GARAGE (ONLY ON FIRST LOAD)
  // =========================================================
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (isTieredUser || categorySlug) return;

    // If URL already has filters, do nothing
    const hasUrlFilters =
      applied.make || applied.model || applied.year || applied.trim || applied.category;

    if (hasUrlFilters) return;

    // Otherwise, apply selected garage vehicle (your previous behavior)
    const saved = loadSelectedGarageVehicle();
    if (!saved) return;

    const params = new URLSearchParams();
    if (saved.make) params.set("make", saved.make);
    if (saved.model) params.set("model", saved.model);
    if (saved.year) params.set("year", String(saved.year));
    if (saved.trim) params.set("trim", saved.trim);

    // Put it into URL so it persists on product -> back
    setSearchParams(params, { replace: true });
  }, [applied, isTieredUser, categorySlug, setSearchParams]);

  // =========================================================
  // ✅ 1) KEEP UI DROPDOWNS IN SYNC WITH URL (BACK/FORWARD)
  // =========================================================
  useEffect(() => {
    setVeh({
      make: applied.make,
      model: applied.model,
      year: applied.year,
      trim: applied.trim,
    });
    setCategory(applied.category);
    setManualOverride(false);
  }, [applied.make, applied.model, applied.year, applied.trim, applied.category]);

  const { make, model, year, trim } = veh;

  // =========================================================
  // 2) DYNAMIC FACETS (Make / Model / Year / Trim / Category)
  // =========================================================
  const dynamicFacetParams = useMemo(
    () => ({
      dynamic: "1",
      ...(make ? { make } : {}),
      ...(model ? { model } : {}),
      ...(year ? { year } : {}),
      ...(trim ? { trim } : {}),
      ...((seoCategory?.databaseValue || category) ? { category: seoCategory?.databaseValue || category } : {}),
    }),
    [make, model, year, trim, category, seoCategory]
  );

  const {
    data: facetsData,
    isLoading: isLoadingFacets,
    error: facetsError,
  } = useGetFitmentFacetsQuery(dynamicFacetParams);

  const makes = useMemo(
    () => (facetsData?.makes ?? []).map((entry) => entry.make).filter(Boolean),
    [facetsData]
  );
  const models = useMemo(
    () => (facetsData?.models ?? []).map((entry) => entry.model).filter(Boolean),
    [facetsData]
  );
  const years = useMemo(
    () =>
      (facetsData?.years ?? [])
        .map((entry) => String(entry))
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b)),
    [facetsData]
  );
  const trims = useMemo(
    () => (facetsData?.trims ?? []).map((entry) => String(entry).trim()).filter(Boolean),
    [facetsData]
  );
  const {
    data: partCategories = [],
    isLoading: isLoadingPartCategories,
    error: partCategoriesError,
  } = useGetPartCategoriesQuery();

  const categoryOptions = useMemo(
    () =>
      (partCategories ?? [])
        .map((cat) => ({
          value: cat.name,
          name: cat.name,
        }))
        .filter((cat) => Boolean(cat.name)),
    [partCategories]
  );
  const selectedCategory =
    categoryOptions.find((cat) => cat.name === category) || null;

  useEffect(() => {
    if (isLoadingFacets || isLoadingPartCategories) return;

    let changed = false;
    let nextCategory = category;
    const yearSet = new Set(years);

    setVeh((prev) => {
      const next = { ...prev };
      if (next.make && !makes.includes(next.make)) {
        next.make = "";
        next.model = "";
        next.year = "";
        next.trim = "";
        changed = true;
      }
      if (next.model && !models.includes(next.model)) {
        next.model = "";
        next.year = "";
        next.trim = "";
        changed = true;
      }
      if (next.year && !yearSet.has(String(next.year))) {
        next.year = "";
        next.trim = "";
        changed = true;
      }
      if (next.trim && !trims.includes(next.trim)) {
        next.trim = "";
        changed = true;
      }
      return changed ? next : prev;
    });

    if (
      nextCategory &&
      !categoryOptions.some((cat) => cat.name === nextCategory)
    ) {
      nextCategory = "";
      setCategory("");
      changed = true;
    }

    if (changed) {
      setManualOverride(true);
    }
  }, [
    isLoadingFacets,
    isLoadingPartCategories,
    makes,
    models,
    years,
    trims,
    categoryOptions,
    category,
  ]);

  // =========================================================
  // ✅ 4) CHANGE HANDLERS (CLEAR DEPENDENTS ON CHANGE)
  // =========================================================
  const handleVehChange = (e) => {
    const { name, value } = e.target;
    setManualOverride(true);

    setVeh((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "make") {
        next.model = "";
        next.year = "";
        next.trim = "";
      } else if (name === "model") {
        next.year = "";
        next.trim = "";
      } else if (name === "year") {
        next.trim = "";
      }

      return next;
    });
  };

  const handleCategoryChange = (e) => {
    setManualOverride(true);
    setCategory(e.target.value);
  };

  // =========================================================
  // ✅ 5) APPLY / CLEAR (WRITE TO URL)
  // =========================================================
  const buildFilterParams = (nextVeh, nextCategory) => {
    const params = new URLSearchParams();

    if (nextVeh.make) params.set("make", nextVeh.make);
    if (nextVeh.model) params.set("model", nextVeh.model);
    if (nextVeh.year) params.set("year", nextVeh.year);
    if (nextVeh.trim) params.set("trim", nextVeh.trim);
    if (nextCategory) params.set("category", nextCategory);

    // reset page on new filters
    return params;
  };

  useEffect(() => {
    if (!manualOverride) return;
    const currentParams = buildFilterParams(
      {
        make: applied.make,
        model: applied.model,
        year: applied.year,
        trim: applied.trim,
      },
      applied.category
    );
    const nextParams = buildFilterParams(veh, category);

    if (nextParams.toString() === currentParams.toString()) return;
    setSearchParams(nextParams);
    setManualOverride(false);
  }, [
    applied.make,
    applied.model,
    applied.year,
    applied.trim,
    applied.category,
    veh,
    category,
    setSearchParams,
    manualOverride,
  ]);

  const clearFilters = () => {
    setManualOverride(true);
    setVeh({ make: "", model: "", year: "", trim: "" });
    setCategory("");
    setSearchParams(new URLSearchParams());
  };

  // Pagination click (keeps current filters)
  const handlePageChange = (page) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("pageNumber");
      if (page === 1) p.delete("page");
      else p.set("page", String(page));
      return p;
    });
  };

  // =========================================================
  // ✅ 6) PRODUCTS QUERY (USES APPLIED FROM URL)
  // =========================================================
  const effectiveKeyword = keyword || '';

  const { data, isLoading, error } = useGetProductsQuery({
    keyword: effectiveKeyword,
    seoCategory: seoCategory?.slug,
    pageNumber: applied.pageNumber,
    make: applied.make || undefined,
    model: applied.model || undefined,
    year: applied.year || undefined,
    trim: applied.trim || undefined,
    category: applied.category || undefined,
  }, { skip: Boolean(categorySlug && !seoCategory) });

  const products = data?.products ?? [];
  const pages = data?.pages || 1;
  const currentPage = data?.page || applied.pageNumber || 1;
  const productCount = data?.products?.length ?? 0;

  const activeFilters = useMemo(() => {
    const filters = [];

    if (effectiveKeyword && !categorySlug) {
      filters.push({ label: "Search", value: effectiveKeyword });
    }
    if (applied.make) {
      filters.push({ label: "Make", value: applied.make });
    }
    if (applied.model) {
      filters.push({ label: "Model", value: applied.model });
    }
    if (applied.year) {
      filters.push({ label: "Year", value: applied.year });
    }
    if (applied.trim) {
      filters.push({ label: "Trim", value: applied.trim });
    }
    if (selectedCategory) {
      filters.push({ label: "Category", value: selectedCategory.name });
    }

    return filters;
  }, [
    effectiveKeyword,
    categorySlug,
    applied.make,
    applied.model,
    applied.year,
    applied.trim,
    selectedCategory,
  ]);

  if (categorySlug && !seoCategory) return <><Meta title="Category Not Found | KCC Parts" noindex /><h1>Category Not Found</h1></>;

  return (
    <>
      <BackButton />
      <Meta
        title={seoCategory?.title || "Auto Parts Catalog | KCC Parts"}
        description={seoCategory?.description}
        jsonLd={seoCategory ? categorySchemas(seoCategory, products) : undefined}
        noindex={Boolean(keyword)}
      />

      {keyword && !categorySlug && (
        <BackButton to="/" className="btn btn-light mb-4" />
      )}

      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
          <h1 className="mb-1">{seoCategory ? seoCategory.databaseValue.replace(/\b\w/g, (m) => m.toUpperCase()) : "All Products"}</h1>
          <p className="text-muted mb-0">
            Find parts that fit your vehicle with precise filters and categories.
          </p>
        </div>
        <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start align-items-sm-center gap-2 text-muted small">
          <div>
            Showing <strong>{productCount}</strong> items
          </div>
          <div>
            Page <strong>{currentPage}</strong> of <strong>{pages}</strong>
          </div>
        </div>
      </div>

      {/* FILTER CARD */}
      <Card className="vehicle-selector mt-4 mb-4 border-0">
        <Card.Header className="bg-white border-0 pb-0">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">Filter your results</h5>
              <small className="text-muted">
                Select vehicle details to narrow down compatible parts. Results update automatically.
              </small>
            </div>
            <div className="all-products-actions d-flex flex-column flex-sm-row gap-2">
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            {/* Category (drives availability for all facets) */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Category (optional)</Form.Label>
              <Form.Select
                value={seoCategory?.databaseValue || category}
                onChange={handleCategoryChange}
                disabled={Boolean(seoCategory) || isLoadingPartCategories || categoryOptions.length === 0}
                className="vehicle-input"
              >
                <option value="">
                  {categoryOptions.length ? "All categories" : "No categories available"}
                </option>
                {categoryOptions.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
              {partCategoriesError && (
                <small className="text-danger">Failed to load categories</small>
              )}
            </Col>

            {/* Make */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Make (optional)</Form.Label>
              {facetsError && (
                <div className="text-danger small mb-1">
                  Failed to load makes
                </div>
              )}
              <Form.Select
                name="make"
                value={make}
                onChange={handleVehChange}
                disabled={isLoadingFacets || makes.length === 0}
                className="vehicle-input"
              >
                <option value="">{makes.length ? "Choose make..." : "No makes available"}</option>
                {makes.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Model */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Model (optional)</Form.Label>
              {facetsError && (
                <div className="text-danger small mb-1">
                  Failed to load models
                </div>
              )}
              <Form.Select
                name="model"
                value={model}
                onChange={handleVehChange}
                disabled={isLoadingFacets || models.length === 0}
                className="vehicle-input"
              >
                <option value="">{models.length ? "Choose model..." : "No models available"}</option>
                {models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Year */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Year (optional)</Form.Label>
              {facetsError && (
                <div className="text-danger small mb-1">
                  Failed to load years
                </div>
              )}
              <Form.Select
                name="year"
                value={year}
                onChange={handleVehChange}
                disabled={isLoadingFacets || years.length === 0}
                className="vehicle-input"
              >
                <option value="">{years.length ? "Choose year..." : "No years available"}</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Trim */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Trim (optional)</Form.Label>
              {facetsError && (
                <div className="text-danger small mb-1">
                  Failed to load trims
                </div>
              )}
              <Form.Select
                name="trim"
                value={trim}
                onChange={handleVehChange}
                disabled={isLoadingFacets || trims.length === 0}
                className="vehicle-input"
              >
                <option value="">
                  {trims.length ? "No trim (optional)" : "No trims (optional)"}
                </option>
                {trims.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Col>

          </Row>
        </Card.Body>
      </Card>

      {activeFilters.length ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
          <span className="text-muted small">Active filters:</span>
          {activeFilters.map((filter) => (
            <Badge
              key={`${filter.label}-${filter.value}`}
              bg="light"
              text="dark"
              className="border"
            >
              {filter.label}: {filter.value}
            </Badge>
          ))}
          <Button variant="link" size="sm" className="px-0" onClick={clearFilters}>
            Clear all
          </Button>
        </div>
      ) : (
        <div className="text-muted mb-4">Browse all products in our catalog.</div>
      )}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">Results</h2>
              <div className="text-muted small">
                {products.length
                  ? `Showing ${products.length} item${products.length === 1 ? "" : "s"}`
                  : "No items available"}
              </div>
            </div>
            {selectedCategory && (
              <div className="text-muted small">
                Category: <strong>{selectedCategory.name}</strong>
              </div>
            )}
          </div>
          <Row className="g-3 mb-4">
            {products.map((product) => (
              <Col key={product._id} sm={12} md={6} lg={4} xl={3} className="d-flex">
                <Product product={product} />
              </Col>
            ))}

            {!products.length && (
              <Col>
                <Message>No products found.</Message>
              </Col>
            )}
          </Row>

          {/* Pagination (URL-driven, preserves filters) */}
          {pages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                {[...Array(pages).keys()].map((x) => {
                  const page = x + 1;
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      href={normalizePublicUrl(`${seoCategory?.path || '/products'}?${new URLSearchParams({ ...Object.fromEntries(searchParams), page: String(page) })}`)}
                      onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
              </Pagination>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AllProductsScreen;
